import { useCallback, useEffect, useRef } from 'react';

// Hold this long (ms) for a long tap
const LONG_TAP_MS = 500;
// Max gap (ms) between lifting the first finger and touching again for a double tap
const DOUBLE_TAP_GAP_MS = 300;
// Finger may drift this far (px) during a touch and still count as a tap (not a scroll)
const MOVE_TOLERANCE_PX = 16;

// Last plain tap { key, time }, shared across instances: the first tap can navigate and
// remount the tapped item, and the second tap must still be recognised as a double tap
let lastTap = null;

const getTouchPoint = (e) => {
    const touch = e.changedTouches[0];
    return { x: touch.clientX, y: touch.clientY, time: Date.now() };
};

const getDistance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// contextmenu raised by a finger long-press (not a mouse right-click). Chrome sends it as a
// PointerEvent with pointerType 'touch'; sourceCapabilities is the fallback for older versions.
const isTouchContextMenu = (e) => {
    const nativeEvent = e.nativeEvent || e;
    return nativeEvent.pointerType === 'touch' ||
        !!nativeEvent.sourceCapabilities?.firesTouchEvents;
};

// Swallow the click the browser may send when the finger is lifted after a long tap.
// The long tap can unmount the tapped item (navigation / view switch), so its own touchend
// can't cancel that click - block it at document level until the next interaction starts.
const suppressNextClick = () => {
    const stopBlocking = () => {
        document.removeEventListener('click', blockClick, true);
        document.removeEventListener('pointerdown', stopBlocking, true);
        document.removeEventListener('keydown', stopBlocking, true);
    };
    const blockClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        stopBlocking();
    };
    document.addEventListener('click', blockClick, true);
    document.addEventListener('pointerdown', stopBlocking, true);
    document.addEventListener('keydown', stopBlocking, true);
};

const isDoubleTap = (key, start) =>
    !!lastTap &&
    lastTap.key === key &&
    start.time - lastTap.time <= DOUBLE_TAP_GAP_MS;

/**
 * Long tap and double tap detection for touch screens.
 *
 * Long tap fires while the finger is still down (after LONG_TAP_MS, or as soon as the
 * browser reports its own long-press), so it works even if the browser cancels the
 * touch afterwards. The click that would follow a recognised gesture is cancelled;
 * plain taps fall through to onClick unchanged.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - When false, no handlers are attached
 * @param {string|number} options.gestureKey - Identifies the item (e.g. its id), both taps of a double tap must hit it
 * @param {Function} options.onLongTap - Called on a long tap
 * @param {Function} options.onDoubleTap - Called on the second tap of a double tap
 * @returns {{ touchHandlers: Object, consumeTouchContextMenu: Function }}
 *   touchHandlers - spread onto the element;
 *   consumeTouchContextMenu(e) - call from onContextMenu, returns true if the event
 *   came from a touch long-press (it is then cancelled and must be ignored)
 */
export const useTouchGestures = ({ enabled, gestureKey, onLongTap, onDoubleTap }) => {
    // Current touch: { start, moved, longTapFired }, null when no finger is down
    const touchRef = useRef(null);
    // Pending long tap timer for the current touch
    const longTapTimerRef = useRef(null);
    // Latest callback, so the timer never calls a stale one
    const onLongTapRef = useRef(onLongTap);
    onLongTapRef.current = onLongTap;

    const clearLongTapTimer = useCallback(() => {
        clearTimeout(longTapTimerRef.current);
        longTapTimerRef.current = null;
    }, []);

    // Clear a pending timer if the item unmounts mid-touch
    useEffect(() => clearLongTapTimer, [clearLongTapTimer]);

    const fireLongTap = useCallback(() => {
        const touch = touchRef.current;
        clearLongTapTimer();
        if (!touch || touch.moved || touch.longTapFired) return;

        touch.longTapFired = true;
        lastTap = null;
        suppressNextClick();
        onLongTapRef.current();
    }, [clearLongTapTimer]);

    const handleTouchStart = useCallback((e) => {
        clearLongTapTimer();
        touchRef.current = {
            start: getTouchPoint(e),
            // A second finger (pinch zoom) is never a tap
            moved: e.touches.length > 1,
            longTapFired: false,
        };
        if (!touchRef.current.moved) {
            longTapTimerRef.current = setTimeout(fireLongTap, LONG_TAP_MS);
        }
    }, [clearLongTapTimer, fireLongTap]);

    const handleTouchMove = useCallback((e) => {
        const touch = touchRef.current;
        if (touch && getDistance(touch.start, getTouchPoint(e)) > MOVE_TOLERANCE_PX) {
            touch.moved = true; // Scrolling - not a tap
            clearLongTapTimer();
        }
    }, [clearLongTapTimer]);

    const handleTouchCancel = useCallback(() => {
        clearLongTapTimer();
        touchRef.current = null;
    }, [clearLongTapTimer]);

    const handleTouchEnd = useCallback((e) => {
        const touch = touchRef.current;
        clearLongTapTimer();
        touchRef.current = null;
        if (!touch) return;

        if (touch.longTapFired) {
            e.preventDefault(); // Cancel the click that would follow the long tap
            return;
        }
        if (touch.moved || e.touches.length > 0) return;

        if (isDoubleTap(gestureKey, touch.start)) {
            lastTap = null;
            e.preventDefault(); // Cancel the second click (no repeat of the single-tap action)
            onDoubleTap(e);
            return;
        }

        // Plain tap - let the click through, remember it for double tap detection
        lastTap = { key: gestureKey, time: Date.now() };
    }, [clearLongTapTimer, gestureKey, onDoubleTap]);

    // The browser fires contextmenu for a long-press while the finger is still down.
    // Cancel it (no native menu / text selection) and treat the touch as a long tap.
    // It can also land on an item that never saw the touch start - e.g. one rendered under
    // the finger after the long tap left focus mode - so it is consumed there too, without
    // firing a long tap.
    const consumeTouchContextMenu = useCallback((e) => {
        if (!enabled) return false;
        if (!touchRef.current && !isTouchContextMenu(e)) return false; // Mouse right-click

        e.preventDefault();
        fireLongTap();
        return true;
    }, [enabled, fireLongTap]);

    const touchHandlers = enabled ? {
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
        onTouchCancel: handleTouchCancel,
    } : {};

    return { touchHandlers, consumeTouchContextMenu };
};
